import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { IPost } from 'types/post.types'
import { IUser, IUserData } from 'types/user.types'
import { FixMeLater } from 'types'
import { BUTTONS, POST } from 'locales/locales'
import { icons } from 'utils/icons'
import { INewsFeedSlice } from 'app/slices/news-feed.slice'
import { useAppSelector } from 'app/hooks'
import colors from 'utils/colors'
import useEditPostInput from './useEditPostInput'

import CustomButton from 'components/button/CustomButton'
import Content from './EditPostContent'
import CustomModal from 'components/modals/CustomModal'
import { useState } from 'react'
import usePasteFile from '../../../utils/hooks/usePasteFile'
import { Switch } from '@progress/kendo-react-inputs'
import CustomText from 'components/text/CustomText'

interface IEditPostProps {
  post: IPost
  userInfo: IUser
  avatar: string
  fullname: string
  onClose: () => void
  onEditComment?: (prev?: FixMeLater) => void
}

const EditPostContent = ({ post, avatar, fullname, onClose, onEditComment }: IEditPostProps) => {
  
  const { t } = useTranslation()

  const { isPostLoading }: INewsFeedSlice = useAppSelector((state) => state.newsFeed)
  const [type, setType] = useState<"photo" | "video" | "file">("photo")
  const [commentToggle, setCommentToggle] = useState(post?.commentToggle)

  const {
    editedContent,
    isValidEditPost,
    onEditPost,
    getInputProps,
    onEditEmojiClick,
    onChangeEditText,
    onCancelEditFile,
    onChangeHeaderInputData,
    onEditedContentFileChange,
    onChangeToggleComment,
  } = useEditPostInput({ post, onClose, type,onEditComment })

  const onChangeInputHeader = (e: FixMeLater) => {
    setHeader(e.value as string)
    onChangeHeaderInputData(e.value as string)
  }

  const onChangeCommentToggle = () => {
    setCommentToggle(!commentToggle)
    onChangeToggleComment(!commentToggle)
  }

  const [header, setHeader] = useState(editedContent.header)

  usePasteFile({ onChangeInputFileData: onEditedContentFileChange })

  return (
    <CustomModal
      okTitle={t(BUTTONS.EDIT)}
      onOk={onEditPost}
      okButtonDisabled={!isValidEditPost}
      closeModal={onClose}
      title={onEditComment ? t(POST.EDIT_COMMENT) : t(POST.EDIT_POST)}
      overflow={'visible'}
      size="large"
      extraButtons={
        onEditComment
          ? undefined
          : [
                <ActionButton
                key={'postFile'}
                text={t(POST.FILE)}
                typography="body4"
                icon={icons.feedFile}
                textColor={colors.gray100}
                isFilePicker
                getInputProps={getInputProps}
                onClick={() => setType("file")}
              />,
              <ActionButton
                key={'postphoto'}
                text={t(POST.PHOTO)}
                typography="body4"
                icon={icons.feedPhoto}
                textColor={colors.gray100}
                isFilePicker
                getInputProps={getInputProps}
                onClick={() => setType("photo")}
              />,
              <ActionButton
                key={'postvideo'}
                text={t(POST.VIDEO)}
                typography="body4"
                icon={icons.feedVideo}
                textColor={colors.gray100}
                isFilePicker
                getInputProps={getInputProps}
                onClick={() => setType("video")}
              />,
              <div
                onClick={onChangeCommentToggle}
                key={'comment'}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Switch
                  checked={commentToggle}
                  defaultChecked={commentToggle}
                  offLabel="0"
                  onLabel="I"
                  size={'small'}
                />
                <div style={{ marginLeft: '20px' }}>
                  <CustomText color={colors.gray100} cursor="pointer" typography="body4">
                    {t(POST.COMMENTS)}
                  </CustomText>
                </div>
              </div>,
            ]
      }
    >
      <Content
        data={editedContent}
        placeHolder={t(POST.EDIT_CONTENT)}
        users={post.userData as IUserData}
        avatar={avatar}
        fullname={fullname}
        header={header}
        isComment={onEditComment ? true : false}
        commentToggle={post.commentToggle ?? true}
        onChangeInputHeader={onChangeInputHeader}
        isValidEditPost={isValidEditPost}
        isPostLoading={isPostLoading}
        onClose={onClose}
        onEditPost={onEditPost}
        onCancelFile={onCancelEditFile}
        onChangeText={onChangeEditText}
        onEmojiClick={onEditEmojiClick}
      />
    </CustomModal>
  )
}

export default EditPostContent

const ActionButton = styled(CustomButton)`
  box-shadow: none;
  height: 28px;
  gap: 8px;
  padding: 0px 20px;
  background-color: ${colors.white};
  &:hover {
    background-color: ${colors.light50};
  }
`
